import {
  Button,
  HStack,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import {
  DataTable,
  ExportButton,
  MenuItemLink,
  ModalInputDatePicker,
  NavBar,
} from "@components/ui-kit";
import { Pagination } from "@components/ui-kit/table/pagination";
import { Trans, t } from "@lingui/macro";
import type { OiWhereColumn } from "@src/__generated__/graphql";
import { ExportFinancialDocumentsButton } from "@src/components/modules/common/exports/financial-documents/ExportFinancialDocumentsButton";
import { XeroExportModal } from "@src/components/modules/common/exports/xero/XeroExportModal";
import { OutgoingInvoicesItemsList } from "@src/components/modules/invoices/outgoing/invoice-items";
import { Icon } from "@src/components/ui-kit/Icon";
import { MarkAsUnpaidConfirm } from "@src/components/widgets/Modals/MarkAsUnpaidConfirm";
import { API_BASE_URL } from "@src/config/constants";
import { FiltersPanel } from "@src/utils/components/filters/components/filters-panel";
import { can } from "@src/utils/components/permissions";
import { currency } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import type { DateRange as Range } from "@src/utils/types";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import { type FC, useContext, useEffect } from "react";
import { ChangeDatesModal } from "../../shared/change-dates-modal";
import { PageOutgoingInvoicesCtx } from "./PageOutgoingInvoices";
import { QuickFilters } from "./StoreOutgoingInvoices";
import { TableOutgoingInvoices } from "./table/TableOutgoingInvoices";

const BoxSummary: FC = observer(function BoxSummary() {
  const store = useContext(PageOutgoingInvoicesCtx);
  const isLoaded = !store.isSummaryLoading;

  const {
    invoices,
    total_amount,
    total_vat,
    total_amount_with_vat,
    payments_due_without_vat,
    payments_due_vat,
    payments_due,
    overdue_without_vat,
    overdue_vat,
    overdue,
    overdue_by_less_than_30_days_without_vat,
    overdue_by_less_than_30_days_vat,
    overdue_by_less_than_30_days,
    overdue_by_31_to_90_days_without_vat,
    overdue_by_31_to_90_days_vat,
    overdue_by_31_to_90_days,
    overdue_by_91_to_365_days_without_vat,
    overdue_by_91_to_365_days_vat,
    overdue_by_91_to_365_days,
    overdue_by_more_than_365_days_without_vat,
    overdue_by_more_than_365_days_vat,
    overdue_by_more_than_365_days,
    paid_amount,
    unpaid_amount,
    billing_amount,
    billing_amount_vat,
    billing_amount_with_vat,
  } = store.outgoingInvoicesSummary ?? {
    invoices: 0,
    total_amount: 0,
    total_vat: 0,
    total_amount_with_vat: 0,
    payments_due_without_vat: 0,
    payments_due_vat: 0,
    payments_due: 0,
    overdue_without_vat: 0,
    overdue_vat: 0,
    overdue: 0,
    overdue_by_less_than_30_days_without_vat: 0,
    overdue_by_less_than_30_days_vat: 0,
    overdue_by_less_than_30_days: 0,
    overdue_by_31_to_90_days_without_vat: 0,
    overdue_by_31_to_90_days_vat: 0,
    overdue_by_31_to_90_days: 0,
    overdue_by_91_to_365_days_without_vat: 0,
    overdue_by_91_to_365_days_vat: 0,
    overdue_by_91_to_365_days: 0,
    overdue_by_more_than_365_days_without_vat: 0,
    overdue_by_more_than_365_days_vat: 0,
    overdue_by_more_than_365_days: 0,
    billing_amount: 0,
    billing_amount_vat: 0,
    billing_amount_with_vat: 0,
    paid_amount: 0,
    unpaid_amount: 0,
  };

  return (
    <DataTable.SummaryBox justify="space-between" hideBg>
      <DataTable.SummaryItem
        label={t`Billing amount`}
        isLoaded={isLoaded}
        labelTooltip={t`Amount credited to billing, at brand or project level.`}
      >
        <Text fontSize="2xl" fontWeight="bold" mb="1">
          {currency.formatByWorkspace(billing_amount)}
        </Text>

        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(billing_amount_vat)}
          &nbsp;
          <Trans>(VAT)</Trans>
        </DataTable.SummaryItemSubText>

        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(billing_amount_with_vat)}
          &nbsp;
          <Trans>(with VAT)</Trans>
        </DataTable.SummaryItemSubText>

        <Text mt="2" fontSize="xs">
          <strong>{invoices}</strong>

          <Text as="span" color="gray.500">
            &nbsp;
            <Trans>invoices</Trans>
          </Text>
        </Text>
      </DataTable.SummaryItem>

      <DataTable.SummaryItem
        isLoaded={isLoaded}
        alignment="right"
        label={t`Paid`}
      >
        {currency.formatByWorkspace(paid_amount)}

        <Text color="gray.500" mt="2">
          <Trans>Unpaid</Trans>
        </Text>
        {currency.formatByWorkspace(unpaid_amount)}
      </DataTable.SummaryItem>

      <DataTable.SummaryItem
        label={t`Current`}
        isLoaded={isLoaded}
        alignment="right"
        labelTooltip={t`VAT breakdown is approximate — invoices may contain items with different VAT rates. The VAT shown is a weighted average across unpaid invoices.`}
      >
        {currency.formatByWorkspace(payments_due_without_vat)}

        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(payments_due_vat)}
          &nbsp;
          <Trans>(VAT)</Trans>
        </DataTable.SummaryItemSubText>

        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(payments_due)}
          &nbsp;
          <Trans>(with VAT)</Trans>
        </DataTable.SummaryItemSubText>
      </DataTable.SummaryItem>

      <DataTable.SummaryItem
        label={t`Overdue`}
        isLoaded={isLoaded}
        alignment="right"
        labelTooltip={
          <Text>
            <Trans>Overdue by less than 30 days:</Trans>
            <br />
            {currency.formatByWorkspace(
              overdue_by_less_than_30_days_without_vat,
            )}
            <br />
            {currency.formatByWorkspace(overdue_by_less_than_30_days_vat)}
            <Trans>(VAT)</Trans>
            <br />
            {currency.formatByWorkspace(overdue_by_less_than_30_days)}&nbsp;
            <Trans>(with VAT)</Trans>
            <br />
            <br />
            <Trans>Overdue by 31-90 days:</Trans>
            <br />
            {currency.formatByWorkspace(overdue_by_31_to_90_days_without_vat)}
            <br />
            {currency.formatByWorkspace(overdue_by_31_to_90_days_vat)}&nbsp;
            <Trans>(VAT)</Trans>
            <br />
            {currency.formatByWorkspace(overdue_by_31_to_90_days)}&nbsp;
            <Trans>(with VAT)</Trans>
            <br />
            <br />
            <Trans>Overdue by 91-365 days:</Trans>
            <br />
            {currency.formatByWorkspace(overdue_by_91_to_365_days_without_vat)}
            <br />
            {currency.formatByWorkspace(overdue_by_91_to_365_days_vat)}&nbsp;
            <Trans>(VAT)</Trans>
            <br />
            {currency.formatByWorkspace(overdue_by_91_to_365_days)}&nbsp;
            <Trans>(with VAT)</Trans>
            <br />
            <br />
            <Trans>Overdue by more than 365 days:</Trans>
            <br />
            {currency.formatByWorkspace(
              overdue_by_more_than_365_days_without_vat,
            )}
            <br />
            {currency.formatByWorkspace(overdue_by_more_than_365_days_vat)}
            &nbsp;
            <Trans>(VAT)</Trans>
            <br />
            {currency.formatByWorkspace(overdue_by_more_than_365_days)}&nbsp;
            <Trans>(with VAT)</Trans>
          </Text>
        }
      >
        {currency.formatByWorkspace(overdue_without_vat)}

        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(overdue_vat)}
          &nbsp;
          <Trans>(VAT)</Trans>
        </DataTable.SummaryItemSubText>

        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(overdue)}
          &nbsp;
          <Trans>(with VAT)</Trans>
        </DataTable.SummaryItemSubText>
      </DataTable.SummaryItem>

      <DataTable.SummaryItem
        label={t`Total amount`}
        isLoaded={isLoaded}
        alignment="right"
        labelTooltip={t`Total value of all issued documents, as recorded in accounting.`}
      >
        {currency.formatByWorkspace(total_amount)}

        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(total_vat)}
          &nbsp;
          <Trans>(VAT)</Trans>
        </DataTable.SummaryItemSubText>

        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(total_amount_with_vat)}
          &nbsp;
          <Trans>(with VAT)</Trans>
        </DataTable.SummaryItemSubText>
      </DataTable.SummaryItem>
    </DataTable.SummaryBox>
  );
});

export const CardListOutgoingInvoices = observer(
  function CardListOutgoingInvoices() {
    const { query } = useRouter();
    const { workspaceStore, UIStore } = useStore();
    const store = useContext(PageOutgoingInvoicesCtx);

    const mrpModal = useDisclosure();
    const cashFlowModal = useDisclosure();
    const prolucModal = useDisclosure();

    useEffect(() => {
      try {
        if ("xero" in query) {
          query.xero === "1" || query.xero === "true"
            ? UIStore.toast({
                title: t`Xero integration successful`,
                status: "success",
              })
            : UIStore.toast({
                title: t`Xero integration failed, please try again later`,
                status: "error",
              });
        }
      } catch (e) {
        console.error(e);
      }
    }, []);

    return (
      <div data-testid="card-list-projects">
        <DataTable>
          <div className="h-24 flex justify-between px-6">
            <NavBar>
              <NavBar.Item
                active={store.activeQuickFilter === QuickFilters.All}
                onClick={() => store.toggleQuickFilter(QuickFilters.All)}
              >
                <Trans>All</Trans>
              </NavBar.Item>

              <NavBar.Item
                active={store.activeQuickFilter === QuickFilters.Invoices}
                onClick={() => store.toggleQuickFilter(QuickFilters.Invoices)}
              >
                <Trans>Invoices</Trans>
              </NavBar.Item>

              <NavBar.Item
                active={
                  store.activeQuickFilter === QuickFilters.PaymentReceipts
                }
                onClick={() =>
                  store.toggleQuickFilter(QuickFilters.PaymentReceipts)
                }
              >
                <Trans>Payment receipts</Trans>
              </NavBar.Item>

              <NavBar.Item
                active={
                  store.activeQuickFilter === QuickFilters.PastDuePayments
                }
                onClick={() =>
                  store.toggleQuickFilter(QuickFilters.PastDuePayments)
                }
              >
                <Trans>Overdue payments</Trans>
              </NavBar.Item>

              <NavBar.Item
                active={store.activeQuickFilter === QuickFilters.Unpaid}
                onClick={() => store.toggleQuickFilter(QuickFilters.Unpaid)}
              >
                <Trans>Unpaid</Trans>
              </NavBar.Item>

              <NavBar.Item
                active={store.isInvoiceItemsActive}
                onClick={() =>
                  store.toggleQuickFilter(QuickFilters.InvoiceItems)
                }
              >
                <Trans>Invoiced budget items</Trans>
              </NavBar.Item>
            </NavBar>

            {!store.isInvoiceItemsActive && (
              <HStack spacing="3">
                {!workspaceStore.settings?.xero_enabled &&
                  workspaceStore.settings?.exportSettings.xero && (
                    <Button
                      as={Link}
                      colorScheme="grey"
                      href={`${API_BASE_URL}/xero/authorization`}
                      isExternal
                      variant="outline"
                    >
                      <Trans>Xero Integration</Trans>
                    </Button>
                  )}
                <Menu>
                  <MenuButton
                    as={ExportButton}
                    isLoading={
                      store.isExporting
                      // store.isVarioExporting ||
                      // store.isPremierExporting ||
                      // store.isOmegaExporting ||
                      // isPohodaExporting
                    }
                    rightIcon={<Icon name="chevron-down" />}
                  />
                  <MenuList>
                    <MenuItem onClick={cashFlowModal.onOpen}>
                      <Trans>Cash flow</Trans>
                    </MenuItem>
                    <MenuItem onClick={store.exportUnpaidOutgoingInvoices}>
                      <Trans>Unpaid invoices</Trans>
                    </MenuItem>
                    <MenuItem onClick={store.exportOutgoingInvoices}>
                      <Trans>Outgoing invoices</Trans>
                    </MenuItem>
                    <Tooltip
                      hasArrow
                      isDisabled={!store.areExportsDisabled}
                      label={t`Select invoices to export`}
                      placement="top"
                    >
                      <MenuItem
                        isDisabled={store.areExportsDisabled}
                        onClick={() =>
                          store.exportAbraFlexi(
                            store.selectedOutgoingInvoicesIds,
                          )
                        }
                      >
                        <Trans>ABRA</Trans>
                      </MenuItem>
                    </Tooltip>
                    <ExportFinancialDocumentsButton />
                    {store.showMrpExport && (
                      <MenuItem onClick={mrpModal.onOpen}>
                        <Trans>MRP</Trans>
                      </MenuItem>
                    )}
                    {store.showProlucExport && (
                      <MenuItem onClick={prolucModal.onOpen}>
                        <Trans>PROLUC</Trans>
                      </MenuItem>
                    )}
                    {store.showXeroExport && (
                      <MenuItem
                        onClick={() => {
                          store.xeroModalRef.current?.open();
                        }}
                      >
                        <Trans>Xero</Trans>
                      </MenuItem>
                    )}
                    {store.showPohodaExport && (
                      <Tooltip
                        hasArrow
                        isDisabled={!store.areExportsDisabled}
                        label={t`Select invoices to export`}
                        placement="top"
                      >
                        <MenuItem
                          isDisabled={store.areExportsDisabled}
                          onClick={store.exportPohoda}
                        >
                          <Trans>Pohoda</Trans>
                        </MenuItem>
                      </Tooltip>
                    )}
                    {workspaceStore.settings?.exportSettings.premier && (
                      <Tooltip
                        hasArrow
                        isDisabled={!store.areExportsDisabled}
                        label={t`Select invoices to export`}
                        placement="top"
                      >
                        <MenuItem
                          isDisabled={store.areExportsDisabled}
                          onClick={() => {
                            store.exportPremier(
                              store.selectedOutgoingInvoicesIds,
                            );
                          }}
                        >
                          <Trans>Premier</Trans>
                        </MenuItem>
                      </Tooltip>
                    )}
                    {workspaceStore.settings?.exportSettings.omega && (
                      <Tooltip
                        hasArrow
                        isDisabled={!store.areExportsDisabled}
                        label={t`Select invoices to export`}
                        placement="top"
                      >
                        <MenuItem
                          isDisabled={store.areExportsDisabled}
                          onClick={() => {
                            store.exportOmega(
                              store.selectedOutgoingInvoicesIds,
                            );
                          }}
                        >
                          <Trans>OMEGA</Trans>
                        </MenuItem>
                      </Tooltip>
                    )}
                    {workspaceStore.settings?.exportSettings.vario && (
                      <Tooltip
                        hasArrow
                        isDisabled={!store.areExportsDisabled}
                        label={t`Select invoices to export`}
                        placement="top"
                      >
                        <MenuItem
                          isDisabled={store.areExportsDisabled}
                          onClick={() =>
                            store.exportToVario(
                              store.selectedOutgoingInvoicesIds,
                            )
                          }
                        >
                          <Trans>Vario</Trans>
                        </MenuItem>
                      </Tooltip>
                    )}
                  </MenuList>
                </Menu>

                <XeroExportModal ref={store.xeroModalRef} />
                <ModalInputDatePicker
                  allowRange
                  title={t`Choose start and end date`}
                  onConfirm={(range: Range) =>
                    store.exportMrp(range.start, range.end)
                  }
                  {...mrpModal}
                  confirmLabel={t`Generate`}
                />

                <ModalInputDatePicker
                  allowRange
                  title={t`Choose start and end date`}
                  onConfirm={(range: Range) =>
                    store.exportProluc(range.start, range.end)
                  }
                  {...prolucModal}
                  confirmLabel={t`Generate`}
                />
                <ModalInputDatePicker
                  allowRange
                  title={t`Choose supply date range`}
                  onConfirm={(range: Range) =>
                    store.exportCashFlow(range.start, range.end)
                  }
                  {...cashFlowModal}
                  confirmLabel={t`Generate`}
                />
                {can("requestForInvoicing_create_all") && (
                  <Menu>
                    <MenuButton
                      as={Button}
                      rightIcon={<Icon name="chevron-down" color="blue.300" />}
                    >
                      <Trans>Add new</Trans>
                    </MenuButton>

                    <MenuList>
                      {can(
                        "requestForInvoicing_create_all",
                        "requestForInvoicing_create_team",
                      ) && (
                        <MenuItemLink
                          href={{
                            pathname: "/invoices/for-invoicing/create-invoice",
                          }}
                        >
                          <Trans>Invoice</Trans>
                        </MenuItemLink>
                      )}

                      {can(
                        "requestForInvoicing_create_all",
                        "requestForInvoicing_create_team",
                      ) && (
                        <MenuItemLink
                          href={{
                            pathname:
                              "/invoices/for-invoicing/create-credit-note",
                          }}
                        >
                          <Trans>Credit note</Trans>
                        </MenuItemLink>
                      )}
                    </MenuList>
                  </Menu>
                )}
              </HStack>
            )}
          </div>

          <hr />

          {store.isInvoiceItemsActive ? (
            <OutgoingInvoicesItemsList />
          ) : (
            <>
              <DataTable.Filter>
                <FiltersPanel<OiWhereColumn>
                  filters={store.where}
                  searchDefaultValue={store.searchTerm}
                  searchPlaceholder={t`Search by invoice no, brand, project`}
                  onSearch={store.setSearchTerm}
                />
              </DataTable.Filter>

              <BoxSummary />

              <div data-testid="table">
                <TableOutgoingInvoices />
                <Pagination store={store.pagination} />
              </div>

              <ChangeDatesModal store={store.changeDatesModalStore} />
              <MarkAsUnpaidConfirm
                isOpen={store.markAsUnpaidModal.isOpen}
                onClose={store.markAsUnpaidModal.onClose}
                hasMatchedPayments={
                  store.markAsUnpaidModal.additionalData?.hasMatchedPayments ??
                  false
                }
                isLoading={store.isMarkingAsUnpaid}
                onConfirm={store.markAsUnpaid}
              />
            </>
          )}
        </DataTable>
      </div>
    );
  },
);
